import { reactive } from 'vue';
import BaseSetupService from '@/setup/BaseSetupService';
import { useNotif } from '@orion.ui/orion';

type Props = SetupProps<typeof OrderDetailsStatusHelperSetupService.props>

export default class OrderDetailsStatusHelperSetupService extends BaseSetupService<Props> {
	static readonly props = {};

	private readonly state = reactive({ activeTab: 'empty' });

	readonly _aside?: OrionAside;

	get activeTab () { return this.state.activeTab; }
	set activeTab (val) { this.state.activeTab = val; }

	get activeTabContent () {
		return this.statusOptions.filter(x => x.state === this.activeTab)[0].content;
	}

	get activeTabState (): StateIcon {
		return this.statusOptions.filter(x => x.state === this.activeTab)[0].state;
	}

	get columnOptions () {
		return [
			{
				label: 'Disponibilités',
				possibleState: ['warning', 'complete'],
			},
			{
				label: 'Qualification',
				possibleState: ['warning', 'complete'],
			},
			{
				label: 'Langues',
				possibleState: ['empty', 'warning', 'complete'],
			},
			{
				label: 'Véhicule',
				possibleState: ['empty', 'warning', 'complete'],
			},
			{
				label: 'Documents',
				possibleState: ['empty', 'partial', 'warning', 'complete'],
			},
			{
				label: 'Candidat',
				possibleState: ['empty', 'partial', 'danger', 'complete'],
			},
			{
				label: 'Client',
				possibleState: ['empty', 'partial', 'danger', 'complete'],
			},
			{
				label: 'Validé',
				possibleState: ['empty', 'complete'],
			},
		];
	}

	get statusOptions () {
		return [
			{
				label: 'Neutre',
				color: 'default',
				state: 'empty' as StateIcon,
				outline: false,
				content: `Cette couleur représente un état <strong> neutre </strong>, aucune action n’a été effectuée, rien ne manque. <br><br>
				Exemple : Si cette pastille est de cette couleur dans le colonne de proposition au candidat, <br>
				cela veut dire qu’aucune proposition n’a été envoyée au candidat.`,
			},
			{
				label: 'En attente',
				color: 'white',
				state: 'partial' as StateIcon,
				outline: true,
				content: `Cette couleur représente <strong>l'attente de réponse </strong> ou d'action de l'intérimaire suite à une action de votre part <br><br>
				Exemple : L'intérimaire n'avait pas partagé les documents requis pour la mission, vous en avez fait la demande de partage <br>
				Lorsque l'intérimaire aura partagé ses documents, la pastille deviendra verte !
				`,
			},
			{
				label: 'Incomplet',
				color: 'warning',
				state: 'warning' as StateIcon,
				outline: false,
				content: `Cette couleur représente un état <strong>incomplet</strong> <br><br>
				Exemple : L'intérimaire ne possède qu'un document requis parmis ceux que vous avez demandé.
				<br>
				`,
			},
			{
				label: 'Refus',
				color: 'danger',
				state: 'danger' as StateIcon,
				outline: false,
				content: `Cette couleur représente un état de <strong>refus d'une proposition</strong> <br><br>
				Exemple : L'intérimaire a refusé la proposition de mission. <br>
				`,
			},
			{
				label: 'Complet',
				state: 'complete' as StateIcon,
				color: 'success',
				outline: false,
				content: `Cette couleur représente l'état <strong>complet ou de succès</strong> <br><br>
				Exemple : L'intérimaire possède tous les documents requis pour la commande.
				`,
			},
		];
	}

	constructor (props?: Props, _aside?: OrionAside) {
		super(props);
		this._aside = _aside;

		this.registerBusEvent('close-aside', () => this._aside?.close());
	}

	isActive (val: string) { return this.state.activeTab === val; };

	handleIconClick (requestType: 'availability' | 'documents') {
		useNotif.info(
			'Une action est disponible',
			requestType === 'availability'
				? `Vous pouvez demander le partage des disponibilités !`
				: `Vous pouvez demander le partage des documents manquants !`,
		);
	}

	setPossibleColor (string: string): StateIcon {
		const columnOption = this.columnOptions.filter(x => x.label === string)[0];
		if (columnOption.possibleState.includes(this.activeTabState)) {
			return this.activeTabState;
		} else return 'empty';
	}

	hasActiveState (string: string): boolean {
		const columnOption = this.columnOptions.filter(x => x.label === string)[0];
		return !!columnOption.possibleState.includes(this.activeTabState);
	}
}
